import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { queryKeys } from '@/lib/queryKeys';

export interface EbayListingListRow {
	id: string;
	item_id: string;
	listing_id: string | null;
	offer_id: string | null;
	status: 'pending' | 'active' | 'ended' | 'error';
	format: 'fixed_price' | 'auction' | null;
	last_refreshed_at: string | null;
	listed_at: string | null;
	end_time: string | null;
	price_cents: number | null;
	last_error: string | null;
	// joined from user_items
	title: string | null;
	asking_price_cents: number | null;
	short_id: string | null;
	thumb_url: string | null;
}

export function useMyEbayListings() {
	const { user } = useAuth();
	return useQuery({
		queryKey: queryKeys.ebay.listings(user?.id),
		enabled: !!user?.id,
		queryFn: async (): Promise<EbayListingListRow[]> => {
			const { data, error } = await (supabase as any)
				.from('ebay_listings')
				.select(`
					id,item_id,listing_id,offer_id,status,format,last_refreshed_at,listed_at,end_time,price_cents,last_error,
					user_items:item_id ( title, asking_price_cents, short_id, user_item_images ( image_url, display_order ) )
				`)
				.eq('user_id', user!.id)
				.order('listed_at', { ascending: false, nullsFirst: false });
			if (error) throw error;
			return ((data as any[]) ?? []).map((r) => {
				const ui = r.user_items ?? {};
				const imgs = (ui.user_item_images ?? []) as Array<{ image_url: string; display_order: number }>;
				const sorted = [...imgs].sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0));
				return {
					id: r.id,
					item_id: r.item_id,
					listing_id: r.listing_id,
					offer_id: r.offer_id,
					status: r.status,
					format: r.format,
					last_refreshed_at: r.last_refreshed_at,
					listed_at: r.listed_at,
					end_time: r.end_time,
					price_cents: r.price_cents,
					last_error: r.last_error,
					title: ui.title ?? null,
					asking_price_cents: ui.asking_price_cents ?? null,
					short_id: ui.short_id ?? null,
					thumb_url: sorted[0]?.image_url ?? null
				} as EbayListingListRow;
			});
		}
	});
}

export function useMyActiveEbayListingsCount() {
	const { user } = useAuth();
	return useQuery({
		queryKey: queryKeys.ebay.activeCount(user?.id),
		enabled: !!user?.id,
		staleTime: 60_000,
		queryFn: async (): Promise<number> => {
			const { count, error } = await (supabase as any)
				.from('ebay_listings')
				.select('id', { count: 'exact', head: true })
				.eq('user_id', user!.id)
				.eq('status', 'active');
			if (error) throw error;
			return count ?? 0;
		}
	});
}

